<template>
	<scroll-frame ref="agendaContainer">
		<template #head><span class="head-title"><sym-icon name="event"/> Toto je table header</span></template>
		<template #default>
			<table class="agenda" :class="clazz" :style="style">
				<thead>
					<tr v-for="r in structure.header">
						<component v-for="c in r.cells" :is="c.component" v-bind="cellBind(c)"></component>
					</tr>
				</thead>
				<tbody>
				<agenda-row v-for="(obj, idx) in pageObjects" item-key="not-yet-supported">
					<tr v-for="r in structure.row" :class="{ 'oddrow':(idx%2 === 1), 'evenrow':(idx%2 === 0) }">
						<component v-for="c in r.cells" :is="c.component" :object="obj" v-bind="cellBind(c)"></component>
					</tr>
				</agenda-row>
				</tbody>
			</table>
		</template>
		<template #tail>
			<agenda-pager v-if="bottomPager" :span="span" :page="pageSize" :total="total" v-model.number="position"></agenda-pager>
		</template>
	</scroll-frame>
</template>

<script setup lang="ts">
import useContainer, {type ContainerProps, containerProps} from "./Container";
import {injectEnv} from "../Environment";
import type {AgendaPart, DataColumn} from "@t/fd/layout/Agenda";
import {computed, onMounted, onUnmounted, provide, ref} from "vue";
import {getter} from "../Part";
import {AGENDA_START_PADDING, CellAPI, useItemsGrid} from "./agenda/AgendaUtils";
import {itemsGridKey, cellApiKey} from "./agenda/AgendaUtils";
import AgendaPager from "./agenda/AgendaPager.vue";
import {useDirectDataLoader} from "./agenda/DirectDataLoader";
import {useAgendaLoader} from "./agenda/AgendaDataLoader";
import type {DataLoader} from "./agenda/DataLoader";
import createSelected, {rowSelectionKey} from "./agenda/RowSelection";
import {getElementMeasure} from "@fd/helpers/DomUtils.ts";
import ScrollFrame from "@fd/containers/agenda/ScrollFrame.vue";
import SymIcon from "@fd/helpers/SymIcon.vue";
import AgendaRow from "@fd/containers/agenda/AgendaRow.vue";

const uprops = defineProps({...containerProps});
const props = uprops as ContainerProps;
const env = injectEnv();

const agendaContainer = ref(null);
const containerWidth = ref<number>(0);	// net width of the container
const ag = computed<AgendaPart>(() => props.part as AgendaPart);
provide(itemsGridKey, ag);
const selected = createSelected();
provide(rowSelectionKey, selected);

const {visible, visibilityEval, clazz, style, componentOfPart, partRef, localData, ro, vm, prop, formData} = useContainer(props);

const value = computed<any[]>(() => {
    return getter(localData.value as FormData, prop.value, formData);
});

const loader:DataLoader = ag.value.mode === "direct" ? useDirectDataLoader(value, ag.value) : useAgendaLoader(env, ag.value, localData, formData);
loader.page.value = ag.value.page || -1;

const paginated = computed<boolean>(() => loader.page.value > 0);
const pageObjects = computed(() => loader.data.value);
const bottomPager = computed<boolean>(() => paginated.value);
const pageSize = computed<number>(() => loader.page.value);
const total = computed<number>(() => loader.total.value);
const position = computed<number>({
	get():number {
		return loader.position.value;
	},
	set(v: number) {
		loader.position.value = v;
	},
});

const { structure, cellBind } = useItemsGrid(ag, containerWidth);

const span = computed<number>(() => structure.value.cols);

const selectable = computed<boolean>(() => {
    return !!ag.value.selectable && Array.isArray(ag.value.identity) && ag.value.identity.length > 0
});

const cellApi:CellAPI = {
	action(name: string, key:number|string, event: MouseEvent): void {
		console.log("CELL ACTION: " + name + " shift=" + event.shiftKey);
	},

	getSorting(colName: string): "no" | "asc" | "desc" {
        const s = loader.view.sort;
        if (Array.isArray(s) && s.length > 0) {
            for (let sc of s) {
                if (sc.column === colName) return sc.desc ? "desc" : "asc";
            }
        }
        return "no";
    },

    setSorting(colName: string, type: "no" | "asc" | "desc", append: boolean | undefined): void {
        if (type === "no") {
            // Remove from the sort
            if (!Array.isArray(loader.view.sort)) return;   // sort isn't set
            const idx = loader.view.sort.findIndex((sc) => sc.column === colName);
            if (idx >= 0) loader.view.sort.splice(idx, 1);
        } else {
            if (!Array.isArray(loader.view.sort) || !append) loader.view.sort = [];
            for (let sc of loader.view.sort) {
                if (sc.column === colName) {
                    sc.desc = (type === "desc");
                    return;
                }
            }
            loader.view.sort.push({ column: colName, desc: (type === "desc") });
        }
    }
}

provide(cellApiKey, cellApi);

function measureContainer(container:HTMLDivElement) {
	if (container != null) {
		const m = getElementMeasure(container);
		containerWidth.value = Math.floor(m.width - (2 * AGENDA_START_PADDING));
	}
}

let resizeObserver = null;

function resizeCallback(entries:ResizeObserverEntry[]) {
	if (entries.length > 0) {
		measureContainer(entries[0].target as HTMLDivElement);
	}
}

function startObserving(el:HTMLDivElement) {
	if (resizeObserver == null) {
		resizeObserver = new ResizeObserver(resizeCallback);
		resizeObserver.observe(el);
		measureContainer(el);
	}
}

function stopObserving() {
	if (resizeObserver != null) {
		resizeObserver.disconnect();
		resizeObserver = null;
	}
}

onMounted(() => {
	startObserving(agendaContainer.value.frame);
});
onUnmounted(() => {
	stopObserving();
});
</script>

<style>
</style>
